import { useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useReducedMotion } from '@hooks/useReducedMotion';
import { TourBulb } from './TourBulb';
import { useEasedRect } from './useEasedRect';
import { holePath, padRect, placeBulb, placeModal, unionRect, type IRect } from './placement';
import type { ITourStep, TourDirection, TourTarget } from './types';
import styles from './PageTour.module.css';

export interface IPageTourProps<C> {
  tourId: string;
  steps: ITourStep<C>[];
  open: boolean;
  context: C;
  onDismiss: (reason: 'skip' | 'done') => void;
}

const SCROLL_MARGIN = '72px'; // clears the sticky top bar

/** `within` scopes the lookup to one subtree, for a step whose target id
 *  also exists elsewhere on the page (a dialog's field the page renders
 *  inline too). */
const elementsFor = (target: TourTarget, within?: string): HTMLElement[] => {
  const scope = within === undefined ? '' : `[data-tour="${within}"] `;
  return (Array.isArray(target) ? target : [target])
    .map((id) => document.querySelector<HTMLElement>(`${scope}[data-tour="${id}"]`))
    .filter((el): el is HTMLElement => el !== null);
};

const rectOf = (els: HTMLElement[]): IRect | null => {
  if (els.length === 0) return null;
  return unionRect(els.map((el) => { const r = el.getBoundingClientRect(); return { x: r.left, y: r.top, w: r.width, h: r.height }; }));
};

/**
 * The page-tour engine. Renders one step of a script over the live page:
 * a sheet with a hole, a ring on the hole, and the bulb beside it. Targets
 * are `data-tour` attributes; a step without one (or whose target is not on
 * the page) renders as a centred card over the whole sheet.
 */
export function PageTour<C>({ tourId, steps, open, context, onDismiss }: IPageTourProps<C>) {
  const active = useMemo(() => steps.filter((s) => !s.skipWhen?.(context)), [steps, context]);
  const anchored = useMemo(() => active.filter((s) => s.target !== undefined), [active]);
  // The current step is held by ID, not by position: the context can add or
  // drop steps while the tour is open (a folder's files arrive, a search
  // empties a board), and a position would silently land on a different
  // step. `null` is the script's first active step.
  const [currentId, setCurrentId] = useState<string | null>(null);
  const index = useMemo(() => {
    if (currentId === null) return 0;
    const direct = active.findIndex((s) => s.id === currentId);
    if (direct !== -1) return direct;
    // The current step was filtered out from under the tour: settle on the
    // nearest earlier step that survived.
    const position = steps.findIndex((s) => s.id === currentId);
    return Math.max(0, active.filter((s) => steps.indexOf(s) < position).length - 1);
  }, [active, steps, currentId]);
  const [bulbSize, setBulbSize] = useState({ w: 340, h: 220 });
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const bulbRef = useRef<HTMLDivElement>(null);
  const step = active[index];

  // Where the spotlight WANTS to be. The rect it actually paints is the eased
  // one below, and the hole, the ring and the bulb all read that single rect —
  // three pieces on one geometry rather than three schedules.
  const measureHole = useCallback((): IRect | null => {
    if (!open || !step?.target) return null;
    const rect = rectOf(elementsFor(step.target, step.within));
    return rect ? padRect(rect, step.pad ?? 6) : null;
  }, [open, step]);

  // A step with a target wants a rectangle even before its element exists:
  // a step inside a dialog or drawer is entered on the click that opens it,
  // a few frames before React mounts its portal, so the spotlight has to keep
  // looking rather than fall back to the centred card for good.
  const wantsRect = open && step?.target !== undefined;
  const reduced = useReducedMotion();
  const { rect: hole, follow } = useEasedRect(measureHole, { reduced, wantsRect });

  const measureBulb = useCallback(() => {
    const el = bulbRef.current;
    if (!el) return;
    const { offsetWidth: w, offsetHeight: h } = el;
    setBulbSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, []);

  const go = useCallback((next: ITourStep<C>, direction: TourDirection) => {
    step?.onLeave?.(context, direction);
    next.onEnter?.(context);
    setCurrentId(next.id);
  }, [context, step]);

  const advance = useCallback(() => {
    const next = active[index + 1];
    if (next === undefined) onDismiss('done');
    else go(next, 'forward');
  }, [active, go, index, onDismiss]);
  // The step's place among the ANCHORED steps — -1 for a modal one, which is
  // how "step 0 of n" and a hidden Back both fall out. Back is hidden on the
  // first anchored step, so it has to be inert on exactly that signal:
  // `index` would assume the script opens with a welcome card.
  const anchoredPos = anchored.findIndex((s) => s === step);
  const number = anchoredPos + 1;
  const isFirstAnchored = anchoredPos <= 0;
  const back = useCallback(() => {
    const previous = active[index - 1];
    if (isFirstAnchored || previous === undefined) return;
    go(previous, 'back');
  }, [active, go, index, isFirstAnchored]);
  const skip = useCallback(() => onDismiss('skip'), [onDismiss]);

  // Scroll the target into view, then let the spotlight chase it. The glide
  // re-measures on every frame it runs, so it follows a smooth scroll all the
  // way to its end — no ladder of timers, and nothing snapping between them.
  useLayoutEffect(() => {
    if (open && step?.target) {
      const first = elementsFor(step.target, step.within)[0];
      if (first && !first.closest('[role="dialog"]')) {
        first.style.scrollMarginTop = SCROLL_MARGIN;
        first.scrollIntoView({ block: step.scroll ?? 'center', behavior: document.visibilityState === 'visible' ? 'smooth' : 'auto' });
      }
    }
    // The hole's lifetime is this step's: closed, or a target-less step, means
    // no hole, and `follow` resets it synchronously here so the frame that
    // follows never paints a stale ring.
    follow();
    measureBulb();
  }, [open, step, follow, measureBulb]);

  // A target that changes size in place — a list growing a row — fires
  // neither scroll nor resize, so the elements themselves are watched.
  // `found` is a dep on purpose: a target inside a dialog mounts after its
  // step is entered, and the observer has to attach once the search picks it up.
  const found = hole !== null;
  useEffect(() => {
    if (!open || !step?.target || !found) return;
    const observer = new ResizeObserver(() => follow());
    elementsFor(step.target, step.within).forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [open, step, found, follow]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      measureBulb();
      follow();
    };
    window.addEventListener('scroll', follow, { passive: true });
    window.addEventListener('scrollend', follow);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', follow);
      window.removeEventListener('scrollend', follow);
      window.removeEventListener('resize', onResize);
    };
  }, [open, follow, measureBulb]);

  // The action step: the real control is the only clickable thing, and its
  // real click is what moves the tour on. The page's own handler runs too.
  useEffect(() => {
    if (!open || !step?.action) return;
    const els = elementsFor(step.action.target, step.within);
    const onClick = () => advance();
    els.forEach((el) => el.addEventListener('click', onClick));
    return () => els.forEach((el) => el.removeEventListener('click', onClick));
  }, [open, step, advance]);

  // The handler reads the CURRENT step and callbacks without being a dep of
  // the effect, so the document listener is attached once per open rather
  // than torn down and re-added on every step — the codebase's
  // useEffectEvent idiom (see ui/Drawer/DrawerCompanion).
  const onTourKey = useEffectEvent((e: KeyboardEvent) => {
    if (e.key === 'Escape') skip();
    else if (e.key === 'ArrowRight' && !step?.action) advance();
    else if (e.key === 'ArrowLeft') back();
  });
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => onTourKey(e);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus goes into the bulb on every step, so on close it has to go back to
  // whatever held it when the tour opened — otherwise a dismissal strands the
  // keyboard at the top of the document. Declared BEFORE the focus-the-bulb
  // effect so it captures the page's focus, not the bulb's.
  const returnFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    // `activeElement` is typed `Element | null`; everything focusable the
    // app renders is an HTMLElement, and the cast is what gives us `.focus()`.
    returnFocus.current = document.activeElement as HTMLElement | null;
    return () => {
      // The page may have re-rendered that element away while the tour ran.
      if (returnFocus.current?.isConnected) returnFocus.current.focus();
      returnFocus.current = null;
    };
  }, [open]);

  useEffect(() => { if (open) bulbRef.current?.focus(); }, [open, index]);

  if (!open || !step) return null;

  const mode = hole ? 'anchored' : 'modal';
  const placed: ReturnType<typeof placeBulb> = hole
    ? placeBulb(hole, bulbSize, viewport, { side: step.side, align: step.align })
    : { ...placeModal(bulbSize, viewport), side: 'bottom', tail: 0 };
  // Placement follows the hole; the footer follows the step's shape. A
  // targeted step whose target is missing is centred like a modal card but
  // keeps the walking footer — it has no `primary`/`secondary` copy to read.
  const footer = step.target === undefined ? 'modal' : 'steps';

  return createPortal(
    <>
      <svg className={styles.sheet} aria-hidden="true">
        <path d={holePath(viewport, hole)} />
      </svg>
      <div
        className={styles.ring}
        data-action={step.action ? '' : undefined}
        style={hole ? { left: hole.x, top: hole.y, width: hole.w, height: hole.h, opacity: 1 } : { opacity: 0 }}
        aria-hidden="true"
      />
      <TourBulb
        ref={bulbRef}
        tourId={tourId}
        step={step}
        number={number}
        total={anchored.length}
        mode={mode}
        footer={footer}
        side={placed.side}
        tail={placed.tail}
        left={placed.left}
        top={placed.top}
        isFirstAnchored={isFirstAnchored}
        isLast={index === active.length - 2}
        onNext={advance}
        onBack={back}
        onSkip={skip}
      />
    </>,
    document.body,
  );
}
